import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

type ToastState = {
  id: number;
  message: string;
  tone: "info" | "success";
} | null;

type ToastContextValue = {
  showToast: (message: string, tone?: "info" | "success") => void;
};

const ToastContext = createContext<ToastContextValue>({ showToast: () => {} });

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState>(null);

  const showToast = useCallback((message: string, tone: "info" | "success" = "info") => {
    const id = Date.now();
    setToast({ id, message, tone });
    window.setTimeout(() => {
      setToast((current) => (current && current.id === id ? null : current));
    }, 2600);
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-8 z-[90] flex justify-center px-4">
        {toast ? (
          <div
            key={toast.id}
            className="animate-pop pointer-events-auto flex items-center gap-3 rounded-md border border-background-300/70 bg-background-50 px-4 py-3 text-sm text-foreground-900"
          >
            <i
              className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                toast.tone === "success"
                  ? "bg-accent-500 text-background-50"
                  : "bg-primary-500 text-background-50"
              }`}
            >
              {toast.tone === "success" ? "ri-check-line" : "ri-information-line"}
            </i>
            <span>{toast.message}</span>
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}